import { Component, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '../../../../../services/translate/translate.service';
import { ServerApiService } from '../../../../../server-api/server-api.service';
import { ReceiveUploadedFileRequest } from '../../../../../models/upload/receive-uploaded-file-request';
import { ReceiveUploadedFileResult } from '../../../../../models/upload/receive-uploaded-file-result';
import { LogService } from '../../../../../services/log/log.service';
import { ToasterService } from '../../../../../shared-components/custom-toaster/toaster.service';
import { OperationResult } from '../../../../../models/server-result/operation-result';
import { HttpErrorResponse } from '@angular/common/http';
import { UploadFileContentType } from '../../../../../models/upload/upload-file-content-type';
import { User } from '../../../../../models/User/user';
import { ActivatedRoute, Router } from '@angular/router';
import {
	DocumentUploadContractType,
	DocumentUploadContractTypeExtensions,
} from '../../../../../models/enums/document-upload-contract-type';
import { UtilitiesService } from '../../../../../services/utils/utilities.service';
import { DocumentsUploadInstructionsModalComponent } from '../instructions-modal/instructions-modal.component';
import { FileUploadComponent } from '../../../../../shared-components/file-upload/file-upload.component';
import { ProductType } from '../../../../../models/enums/product-type';
import { Step } from '../../../../../shared-components/custom-stepper/custom-stepper.component';
import { RouteAuthorizationService } from '../../../../../services/authorization/route-authorization.service';
import { AppContextService } from '../../../../../services/app-context.service';
import { CustomModalService } from '../../../../../shared-components/custom-modal/custom-modal.service';

@Component({
	selector: 'app-documents-upload-step2',
	templateUrl: './documents-upload-step2.component.html',
	standalone: false,
})
export class DocumentsUploadStep2Component implements OnInit {
	@ViewChild('fileUpload') fileUpload!: FileUploadComponent;

	public fileName: string;
	private fileType: string;
	private fileContent: string;
	public isPerformingRequest = false;
	private uploadContractType: DocumentUploadContractType | null = null;
	private fileRelatedContractId: string;
	private loggedUser: User | null = new User();

	currentStep: number = 1;
	steps: Step[] = [];

	constructor(
		private translateService: TranslateService,
		private serverApiService: ServerApiService,
		private logger: LogService,
		private toasterService: ToasterService,
		private router: Router,
		private route: ActivatedRoute,
		private appContext: AppContextService,
		private utilitiesService: UtilitiesService,
		private modalService: CustomModalService,
		private routeAuthorizationService: RouteAuthorizationService
	) {
		this.initializeSteps();
	}

	initializeSteps(): void {
		this.steps = [
			{
				label: this.translateService.translate('documents-upload-wizard-steps.Step1Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('documents-upload-wizard-steps.Step2Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('documents-upload-wizard-steps.Step3Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('documents-upload-wizard-steps.Step4Label', 'client'),
				icon: 'fa-solid fa-check',
			},
		];
	}

	ngOnInit() {
		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;
			if (this.canAccessStep2()) {
				this.fileName = this.translateService.translate('document-upload-step2.DocumentUploadNoSelectedFile', 'client');

				this.route.params.subscribe((params) => {
					const uploadContractTypeRouteParam = params['uploadContractType'] || '';
					this.fileRelatedContractId = params['contractId'] || '';

					if (!!uploadContractTypeRouteParam) {
						this.uploadContractType = DocumentUploadContractTypeExtensions.FromRouteParam(uploadContractTypeRouteParam);
					}

					if (!this.uploadContractType || !this.fileRelatedContractId) {
						this.logger.error('Missing params on documents upload step2.', this.uploadContractType, this.fileRelatedContractId);
						this.toasterService.showError(
							this.translateService.translate('documents-upload-step2.missing-params-on-documents-upload-step2', 'client')
						);
						return;
					}
				});
			} else {
				this.routeAuthorizationService.routeOnUnauthorized();
			}
		});
	}

	private navigateToStep3(fileId: string): void {
		this.router.navigate([
			'/private/files-upload/upload/documents/step3',
			fileId,
			DocumentUploadContractTypeExtensions.ToRouteParam(this.uploadContractType!),
			this.fileRelatedContractId,
		]);
	}

	protected redirectToDocumentsStep1(): void {
		let uploadContractTypeRouteParam = DocumentUploadContractTypeExtensions.ToRouteParam(this.uploadContractType!);
		let url = `/private/files-upload/upload/documents/step1/${uploadContractTypeRouteParam}?contractId=${this.fileRelatedContractId}`;
		this.router.navigateByUrl(url);
	}

	protected processUploadedDocument(): void {
		const uploadFileContentType = this.getUploadFileContentType();
		const fileRelatedContractProductType = this.getContractProductType();
		if (!uploadFileContentType || !fileRelatedContractProductType) {
			this.logger.error(`Invalid or unexpected document upload contract type = ${this.uploadContractType}`);
			this.toasterService.showError(`Invalid or unexpected document upload contract type = ${this.uploadContractType}`);
			return;
		}

		const receiveUploadedFileRequest: ReceiveUploadedFileRequest = <ReceiveUploadedFileRequest>{
			FileContent: this.fileContent,
			FileName: this.fileName,
			UploadFileContentType: uploadFileContentType!,
			FileType: this.fileType,
			FileRelatedContractId: this.fileRelatedContractId,
			FileRelatedContractProductType: fileRelatedContractProductType!,
			CompanyId: null,
		};

		this.isPerformingRequest = true;
		this.serverApiService.receiveUploadedFile(receiveUploadedFileRequest).subscribe({
			next: (operationResult: OperationResult<ReceiveUploadedFileResult>) => {
				this.isPerformingRequest = false;
				if (operationResult.IsValid) {
					this.navigateToStep3(operationResult.Data.FileId);
				} else {
					const errors = this.utilitiesService.GetDistinctOperationErrors(operationResult.Errors);
					this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
						errors: errors,
						autoClose: false,
					});
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.isPerformingRequest = false;
				this.logger.error('[DocumentsUploadStep2Component::receiveUploadedFileRequest] HttpErrorResponse: ', httpErrorResponse);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
			},
		});
	}

	protected onFileChange(event): void {
		let reader = new FileReader();
		if (event.target.files && event.target.files.length > 0) {
			let file = event.target.files[0];
			reader.readAsDataURL(file);
			reader.onload = () => {
				this.fileName = file.name;
				this.fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
			};
			reader.onloadend = () => {
				if (reader.result !== null && reader.result !== undefined && reader.result.toString() !== null) {
					var base64result = (reader.result as string).split(',')[1];
					this.fileContent = base64result;
					this.processUploadedDocument();
				}
			};
		}
	}

	protected handleFilesChange(files: File[]): void {
		if (files.length === 0) {
			this.fileName = this.translateService.translate('document-upload-step2.DocumentUploadNoSelectedFile', 'client');
			this.fileContent = '';
			this.fileType = '';
		}
	}

	protected selectFile(): void {
		this.fileUpload.openFileDialog();
	}

	private getUploadFileContentType(): UploadFileContentType | null {
		if (this.uploadContractType === DocumentUploadContractType.Confirming) {
			return UploadFileContentType.ConfirmingDocuments_Type1_XLSX;
		} else if (this.uploadContractType === DocumentUploadContractType.Factoring) {
			return UploadFileContentType.FactoringDocuments_Type1_XLSX;
		} else {
			this.logger.warn(
				'[DocumentsUploadStep2Component::getUploadFileContentType] unexpected UploadFileContentType>',
				this.uploadContractType
			);
			return null;
		}
	}

	private getContractProductType(): ProductType | null {
		if (this.uploadContractType === DocumentUploadContractType.Confirming) {
			return ProductType.Confirming;
		} else if (this.uploadContractType === DocumentUploadContractType.Factoring) {
			return ProductType.Factoring;
		} else {
			this.logger.warn(
				'[DocumentsUploadStep2Component::getContractProductType] unexpected UploadFileContentType>',
				this.uploadContractType
			);
			return null;
		}
	}

	protected openInstructionsModal(): void {
		const instructionsModalRef = this.modalService.open<string, { uploadContractType: DocumentUploadContractType | null }>({
			size: 'lg',
			title: this.translateService.translate('documents-upload-instructions-modal.title', 'client'),
			body: {
				kind: 'component',
				componentType: DocumentsUploadInstructionsModalComponent,
			},
			actions: [
				{
					id: 'start',
					label: this.translateService.translate('button.start', 'client') || 'Start',
					color: 'primary',
					variant: 'outlined',
					autoFocus: true,
					run: ({ bodyComponent, modalRef }) => {
						const result = bodyComponent?.start();
						modalRef.close(result);
					},
				},
			],
			data: {
				uploadContractType: this.uploadContractType,
			},
		});

		instructionsModalRef.result.catch(() => undefined);
	}

	private canAccessStep2(): boolean {
		return (
			!!this.loggedUser &&
			!this.loggedUser.IsBackOfficeOperator &&
			(this.loggedUser.canUploadDocumentsForConfirming() || this.loggedUser.canUploadDocumentsForFactoring())
		);
	}
}
